export type Environment = 'development' | 'test' | 'production';
export type Platform = 'all' | 'wechat' | 'douyin' | 'taptap';

export interface AdminActor {
  userId: string;
  username: string;
  displayName: string;
  role: 'viewer' | 'editor' | 'publisher' | 'admin';
}

export interface AdminPlayer {
  playerId: string;
  displayName: string;
  platform: string;
  avatarSource: 'preset' | 'platform';
  avatarPresetId: string;
  avatarUrl: string | null;
  profileUpdatedAt: string | null;
  createdAt: string;
  lastActiveAt: string;
  lastLoginAt: string | null;
}

export interface AnalyticsSummary {
  dateKey: string;
  environment: Environment;
  dau: number;
  loginCount: number;
  sessions: number;
  totalActiveSeconds: number;
  averageActiveSeconds: number;
  loginAttempts: number;
  loginSuccesses: number;
  loginFailures: number;
  loginSuccessRate: number;
  gameplay: Record<string, number>;
  events: Record<string, number>;
  platforms: Array<{ platform: string; events: number }>;
  clientVersions: Array<{ clientVersion: string; events: number }>;
  contentVersions: Array<{ contentVersion: string; events: number }>;
  ruleVersions: Array<{ ruleVersion: string; events: number }>;
}

export interface EconomyActivityPlayer {
  playerId: string;
  displayName: string;
  platform: string;
  fishEarned: number;
  fishSpent: number;
  toolsGranted: number;
  toolsUsed: number;
}

export interface EconomyActivitySummary {
  dateKey: string;
  totalPlayers: number;
  playersWithActivity: number;
  fishEarned: number;
  fishSpent: number;
  toolsGranted: number;
  toolsUsed: number;
  limit: number;
  offset: number;
  players: EconomyActivityPlayer[];
}

export interface EconomyActivityEvent {
  id: string;
  kind: 'fish' | 'tool_granted' | 'tool_used';
  source: string;
  fishDryDelta: number | null;
  toolId: string | null;
  quantity: number;
  balanceAfter: number | null;
  occurredAt: string;
}

export interface LevelAnalyticsRow {
  levelId: string;
  starts: number;
  successes: number;
  failures: number;
  exits: number;
  completionRate: number;
  averageDurationMs: number;
  averageStars: number;
  toolUses: number;
}

export interface LiveVersion {
  configVersionId: string;
  releaseVersion: string;
  environment: Environment;
  ruleVersion: string;
  economyVersion: string;
  contentVersion: string;
  sha256: string;
  payload: LiveConfig;
  createdBy: string;
  publishedAt: string;
}

export type DailyBenefitToolId = 'time_freeze' | 'cat_rescue' | 'carrier_clear' | 'carrier_cell_cut';
export type DailyBenefitRewardItem =
  | { type: 'fish_dry' | 'stamina'; amount: number }
  | { type: 'tool_entitlement'; toolId: DailyBenefitToolId; count: number };
export interface DailyBenefitRewardBundle { items: DailyBenefitRewardItem[] }
export interface DailyBenefitEntry {
  entryId: string;
  displayName: string;
  weight: number;
  reward: DailyBenefitRewardBundle;
}
export interface DailyBenefitConfig {
  activityId: string;
  displayName: string;
  enabled: boolean;
  requiredFeatureId: string;
  adPlacementId: string;
  doubleAdPlacementId?: string;
  secondPickMode: 'reshuffle' | 'remaining' | 'reshuffle_remaining' | 'double_or_reshuffle';
  configVersion: string;
  effectiveFrom: string;
  ordinaryEntries: DailyBenefitEntry[];
  rareEntries: DailyBenefitEntry[];
}

export type DailySignInRewardItem = DailyBenefitRewardItem;
export type DailySignInRewardBundle = DailyBenefitRewardBundle;
export interface DailySignInWeeklyEntry {
  entryId: string;
  displayName: string;
  reward: DailySignInRewardBundle;
}
export interface DailySignInBonusEntry extends DailySignInWeeklyEntry { weight: number }
export interface DailySignInConfig {
  activityId: string;
  displayName: string;
  enabled: boolean;
  requiredFeatureId: string;
  configVersion: string;
  effectiveFrom: string;
  minClientVersion: string;
  platforms: Record<'wechat' | 'douyin' | 'taptap', boolean>;
  makeupAdPlacementId: string;
  bonusAdPlacementId: string;
  probabilityDisclosure: string;
  weeklyRewards: DailySignInWeeklyEntry[];
  bonusEntries: DailySignInBonusEntry[];
}

export interface SocialRewardConfig {
  activityId: string;
  displayName: string;
  enabled: boolean;
  requiredFeatureId: string;
  configVersion: string;
  minClientVersion: string;
  platforms: Record<'wechat' | 'douyin' | 'taptap', boolean>;
  reward: DailyBenefitRewardBundle;
}

export type AdPlacementConfig = LiveConfig['ads'][number];

export interface LiveConfig {
  schemaVersion: 1;
  gameId: string;
  releaseVersion: string;
  ruleVersion: string;
  settlementVersion: number;
  requiredCapabilities: string[];
  weeklyCompetition?: {
    schemaVersion: 1;
    configVersion: string;
    enabled: boolean;
    points: { normal: number; hard: number; superHard: number };
    rankRewards: Array<{ fromRank: number; toRank: number; reward: DailyBenefitRewardBundle }>;
  };
  social?: {
    configVersion:string;enabled:boolean;activeFrom?:string|null;activeUntil?:string|null;qualifyingLevel:number;
    inviteReward:DailyBenefitRewardBundle;recallReward:DailyBenefitRewardBundle;
    milestones:Array<{count:number;reward:DailyBenefitRewardBundle}>;friendLimit:number;
    staminaSendDailyLimit:number;staminaReceiveDailyLimit:number;helpRequestDailyLimit:1;helpResponseDailyLimit:number;
    helpToolPool:string[];recallInactiveDays:number;recallCooldownDays:number;
  };
  homeFeatures: Array<{
    featureId: string;
    displayName: string;
    enabled: boolean;
    unlockLevel: number;
    placement: 'side' | 'bottom';
    order: number;
    platforms: Record<'wechat' | 'douyin' | 'taptap', boolean>;
    minClientVersion: string;
  }>;
  economy: Record<string, unknown> & { version: string; initialFishDry: number; defaultFirstClearReward: number };
  ads: Array<{
    placementId: string;
    displayName: string;
    enabled: Record<'wechat' | 'douyin' | 'taptap', boolean>;
    adUnitIds: Record<'wechat' | 'douyin' | 'taptap', string>;
    dailyLimit: number;
    cooldownSeconds: number;
    rewardPolicy: unknown;
  }>;
  activities: {
    tasks: unknown[];
    bundles: unknown[];
    dailyChallenges: unknown[];
    mysteryBoxes: unknown[];
    dailyBenefit?: DailyBenefitConfig;
    dailySignIn?: DailySignInConfig;
    gameClubDailyReward?: SocialRewardConfig;
    favoriteReward?: SocialRewardConfig;
  };
  content: { contentVersion: string; manifestUrl: string; catalogChecksum: string; totalLevels: number; minClientVersion: string };
}

export interface BoxLeaderboardBoardConfig {
  boardId: 'daily_profit' | 'weekly_profit' | 'total_assets'; enabled: boolean; displayName: string;
  metric: 'net_profit_sum' | 'wallet_balance'; period: 'daily' | 'weekly' | 'lifetime';
  modeScope: 'per_mode' | 'global'; limit: number; rewardCopy: string;
}
export interface BoxLeaderboardConfig {
  schemaVersion: 1; configVersion: string; enabled: boolean; timezone: 'Asia/Shanghai';
  dailyResetHour: number; weeklyResetDay: 1; weeklyResetHour: number; boards: BoxLeaderboardBoardConfig[];
}

export interface LevelDraft {
  levelDraftId: string;
  levelId: string;
  levelNumber: number;
  environment: Environment;
  revision: number;
  payload: LevelPayload;
  validation: { valid: boolean; issues: Array<{ path: string; message: string }> };
  updatedAt: string;
}

export interface ContentVersion {
  contentVersionId: string;
  contentVersion: string;
  manifestUrl: string;
  catalogChecksum: string;
  totalLevels: number;
  publishedAt: string;
}

export interface LevelPayload {
  levelId: string;
  levelNumber: number;
  columns: number;
  rows: number;
  timeLimitSec: number;
  rewardFishDry: number;
  voidCells: Array<{ col: number; row: number }>;
  blockedCells: Array<{ col: number; row: number }>;
  goalCells: Array<{ col: number; row: number }>;
  cats: Array<{ id: string; tag: string; cell: { col: number; row: number } }>;
  carriers: Array<{
    id: string; tag: string; origin: { col: number; row: number };
    footprint: Array<{ col: number; row: number }>; entryOffset: { col: number; row: number }; capacity: number;
  }>;
}

export interface WeeklyAdminState {
  nextSeasonId: string;
  scoringAvailable: boolean;
  verifierVersion?: string | null;
  template: NonNullable<LiveConfig['weeklyCompetition']>;
  current: { seasonId: string; config: NonNullable<LiveConfig['weeklyCompetition']>; finalizedAt: string | null };
  publications: Array<{ config: NonNullable<LiveConfig['weeklyCompetition']>; effectiveSeasonId: string; publishedAt: string; requestId: string }>;
}

export class AdminApiError extends Error {
  constructor(message: string, readonly status: number, readonly code: string) { super(message); }
}

export class AdminApi {
  constructor(private readonly baseUrl = '') {}

  health() {
    return this.request<{ ok: boolean; environment: Environment; deploymentTargetId: string }>('/health');
  }
  competition(gameId: string, mode: 'daily' | 'weekly') {
    return this.request<WeeklyAdminState & { draft: WeeklyAdminState['template'] | null }>(`/v1/admin/games/${gameId}/competitions/${mode}`);
  }
  saveCompetitionDraft(gameId: string, mode: 'daily' | 'weekly', config: WeeklyAdminState['template'], requestId: string) {
    return this.request<{draft: WeeklyAdminState['template']}>(`/v1/admin/games/${gameId}/competitions/${mode}/draft`, { method: 'POST', body: JSON.stringify({config,requestId}) });
  }
  publishCompetition(gameId: string, mode: 'daily' | 'weekly', config: WeeklyAdminState['template'], requestId: string) {
    return this.request<{publication: WeeklyAdminState['publications'][number]}>(`/v1/admin/games/${gameId}/competitions/${mode}/publish`, {method:'POST',body:JSON.stringify({config,requestId})});
  }
  weeklyCompetition(gameId: string) {
    return this.request<WeeklyAdminState>(`/v1/admin/games/${gameId}/weekly-competition`);
  }
  boxLeaderboards() { return this.request<{config:BoxLeaderboardConfig;template:BoxLeaderboardConfig}>('/v1/admin/games/box-bottom-value/box-leaderboards'); }
  saveBoxLeaderboardDraft(environment:Environment,payload:BoxLeaderboardConfig) { return this.request<{draft:{draftId:string;validation:{valid:boolean;issues:unknown[]}}}>('/v1/admin/games/box-bottom-value/box-leaderboards/drafts',{method:'POST',body:JSON.stringify({environment,payload})}); }
  publishBoxLeaderboard(environment:Environment,draftId:string,action:'publish'|'rollback'='publish') { return this.request<{config:BoxLeaderboardConfig}>('/v1/admin/games/box-bottom-value/box-leaderboards/publish',{method:'POST',body:JSON.stringify({environment,draftId,action})}); }
  publishWeeklyCompetition(gameId: string, config: NonNullable<LiveConfig['weeklyCompetition']>, requestId: string) {
    return this.request<{ publication: WeeklyAdminState['publications'][number] }>(`/v1/admin/games/${gameId}/weekly-competition/publish`, {
      method: 'POST', body: JSON.stringify({ config, requestId }),
    });
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body) headers.set('content-type', 'application/json');
    const csrf = cookie('miao_admin_csrf');
    if (csrf && init.method && init.method !== 'GET') headers.set('x-csrf-token', csrf);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
    const response = await fetch(`${this.baseUrl}${path}`, { ...init, headers, credentials: 'include', signal: controller.signal });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new AdminApiError(body?.error?.message ?? `请求失败 (${response.status})`, response.status, body?.error?.code ?? 'HTTP_ERROR');
    return body as T;
    } catch (error) {
      if (controller.signal.aborted) throw new AdminApiError('服务器连接超时，请检查本地服务是否启动后重试', 408, 'NETWORK_TIMEOUT');
      throw error;
    } finally { clearTimeout(timer); }
  }

  me() { return this.request<{ actor: AdminActor }>('/v1/admin/session/me'); }
  login(username: string, password: string) {
    return this.request<{ actor: AdminActor }>('/v1/admin/session/login', {
      method: 'POST', body: JSON.stringify({ username, password }),
    });
  }
  logout() { return this.request('/v1/admin/session/logout', { method: 'POST', body: '{}' }); }

  summary(gameId: string, dateKey: string, environment: Environment, platform: Platform = 'all') {
    return this.request<AnalyticsSummary>(`/v1/admin/games/${gameId}/analytics-summary?dateKey=${dateKey}&environment=${environment}${platform === 'all' ? '' : `&platform=${platform}`}`);
  }
  async exportSummary(gameId: string, dateKey: string, environment: Environment, platform: Platform = 'all') {
    const csrf = cookie('miao_admin_csrf');
    const headers = new Headers();
    if (csrf) headers.set('x-csrf-token', csrf);
    const response = await fetch(
      `${this.baseUrl}/v1/admin/games/${gameId}/analytics-summary.csv?dateKey=${dateKey}&environment=${environment}${platform === 'all' ? '' : `&platform=${platform}`}`,
      { credentials: 'include', headers },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message ?? `导出失败 (${response.status})`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${gameId}-${environment}-${platform}-${dateKey}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }
  economy(gameId: string, dateKey: string, environment: Environment, platform: Platform = 'all') {
    return this.request<{ dateKey: string; sources: FlowRow[]; sinks: FlowRow[] }>(`/v1/admin/games/${gameId}/economy-summary?dateKey=${dateKey}&environment=${environment}${platform === 'all' ? '' : `&platform=${platform}`}`);
  }
  economyActivity(gameId: string, dateKey: string, environment: Environment, platform: Platform, limit: number, offset: number) {
    const query = new URLSearchParams({ dateKey, environment, limit: String(limit), offset: String(offset) });
    if (platform !== 'all') query.set('platform', platform);
    return this.request<EconomyActivitySummary>(`/v1/admin/games/${gameId}/economy-activity?${query}`);
  }
  economyActivityEvents(gameId: string, playerId: string, dateKey: string, environment: Environment, offset = 0) {
    const query = new URLSearchParams({ dateKey, environment, limit: '100', offset: String(offset) });
    return this.request<{ events: EconomyActivityEvent[]; hasMore: boolean }>(
      `/v1/admin/games/${gameId}/economy-activity/${encodeURIComponent(playerId)}/events?${query}`,
    );
  }
  levelSummary(gameId: string, dateKey: string, environment: Environment, platform: Platform = 'all') {
    return this.request<{ dateKey: string; levels: LevelAnalyticsRow[] }>(`/v1/admin/games/${gameId}/level-summary?dateKey=${dateKey}&environment=${environment}${platform === 'all' ? '' : `&platform=${platform}`}`);
  }
  platforms(gameId: string) {
    return this.request<{ platforms: Array<{ platform: string; players: number; lastLoginAt: string }> }>(`/v1/admin/games/${gameId}/platform-summary`);
  }
  players(gameId: string, platform: Platform) {
    const query = platform === 'all' ? '' : `?platform=${platform}`;
    return this.request<{ players: AdminPlayer[] }>(`/v1/admin/games/${gameId}/players${query}`);
  }
  versions(gameId: string, environment: Environment, releaseKey?: string) {
    const query = new URLSearchParams({ environment });
    if (releaseKey) query.set('releaseKey', releaseKey);
    return this.request<{ active: LiveVersion | null; versions: LiveVersion[]; activationHistory: unknown[]; clientReleaseKeys?: string[] }>(`/v1/admin/games/${gameId}/config/versions?${query}`);
  }
  configTemplate(gameId: string) {
    return this.request<{ payload: LiveConfig }>(`/v1/admin/games/${gameId}/config/bootstrap`);
  }
  async latestWeeklyDraft(gameId: string, environment: Environment) {
    const response = await this.request<{ drafts: Array<{ name: string; payload: LiveConfig }> }>(`/v1/admin/games/${gameId}/config/drafts?environment=${environment}&limit=100`);
    return response.drafts.find(draft => draft.name.startsWith('周赛准备 · ') && draft.payload.weeklyCompetition)?.payload ?? null;
  }
  createDraft(gameId: string, name: string, environment: Environment, payload: LiveConfig) {
    return this.request<{ draft: { draftId: string; revision: number; validation: { valid: boolean; issues: unknown[] } } }>(`/v1/admin/games/${gameId}/config/drafts`, {
      method: 'POST', body: JSON.stringify({ name, environment, payload }),
    });
  }
  publishDraft(gameId: string, draftId: string, activate: boolean, releaseKey?: string) {
    return this.request<{ version: LiveVersion }>(`/v1/admin/games/${gameId}/config/drafts/${draftId}/publish`, {
      method: 'POST', body: JSON.stringify({ requestId: crypto.randomUUID(), activate, ...(releaseKey ? { releaseKey } : {}) }),
    });
  }
  activate(gameId: string, versionId: string, environment: Environment, action: 'publish' | 'promote' | 'rollback', releaseKey?: string) {
    return this.request(`/v1/admin/games/${gameId}/config/versions/${versionId}/activate`, {
      method: 'POST', body: JSON.stringify({ requestId: crypto.randomUUID(), environment, action, ...(releaseKey ? { releaseKey } : {}) }),
    });
  }
  levels(gameId: string, environment: Environment) {
    return this.request<{ levels: LevelDraft[] }>(`/v1/admin/games/${gameId}/levels?environment=${environment}`);
  }
  saveLevel(gameId: string, environment: Environment, payload: LevelPayload, expectedRevision?: number) {
    return this.request<{ draft: LevelDraft }>(`/v1/admin/games/${gameId}/levels/${payload.levelId}`, {
      method: 'PUT', body: JSON.stringify({ environment, payload, ...(expectedRevision ? { expectedRevision } : {}) }),
    });
  }
  contentVersions(gameId: string) {
    return this.request<{ versions: ContentVersion[] }>(`/v1/admin/games/${gameId}/content/versions`);
  }
  publishLevels(
    gameId: string,
    input: {
      environment: Environment;
      contentVersion: string;
      releaseVersion: string;
      releaseKey?: string;
      baseContentVersion: string;
      totalLevels: number;
      activate: boolean;
    },
  ) {
    return this.request<{ version: ContentVersion; uploadedObjects: number; configRelease: { version: LiveVersion } }>(`/v1/admin/games/${gameId}/levels/publish`, {
      method: 'POST', body: JSON.stringify({ requestId: crypto.randomUUID(), ...input }),
    });
  }
  targets() { return this.request<{ targets: Array<Record<string, unknown>> }>('/v1/admin/system/targets'); }
  upsertTarget(input: {
    targetId: string; gameId: string; environment: Environment; displayName: string;
    publicBaseUrl: string; healthUrl: string; databaseLabel: string; enabled: boolean;
  }) {
    return this.request<{ target: Record<string, unknown> }>('/v1/admin/system/targets', {
      method: 'PUT', body: JSON.stringify(input),
    });
  }
  routes() { return this.request<{ routes: Array<Record<string, unknown>> }>('/v1/admin/system/routes'); }
  upsertRoute(input: { gameId: string; platform: Exclude<Platform, 'all'>; appId: string; releaseKey: string; targetId: string; enabled: boolean }) {
    return this.request<{ route: Record<string, unknown> }>('/v1/admin/system/routes', {
      method: 'PUT', body: JSON.stringify(input),
    });
  }
  createUser(input: { username: string; displayName: string; password: string; role: AdminActor['role'] }) {
    return this.request<{ user: AdminActor; totpSecret: string; provisioningUri: string }>('/v1/admin/system/users', {
      method: 'POST', body: JSON.stringify(input),
    });
  }
  audit() { return this.request<{ entries: Array<Record<string, unknown>> }>('/v1/admin/system/audit'); }
}

export interface FlowRow { source: string; amount: number; transactions: number }

function cookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}
